import type { ChartConfig } from '@/components/ui/chart';
import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from '@/components/ui/chart';
import { usePropertyContext } from '@/context/propertyContext';
import { useGetDailyRateRecsQuery } from 'features/rules/seasons/gql/_gen_/prop-seasons.gql';
import { useEffect, useMemo, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, XAxis, YAxis } from 'recharts';

const chartConfig: ChartConfig = {
	conservative: {
		label: 'Conservative',
		color: 'hsl(var(--chart-2))',
	},
	neutral: {
		label: 'Neutral',
		color: 'hsl(var(--chart-3))',
	},
	aggressive: {
		label: 'Aggressive',
		color: 'hsl(var(--chart-1))',
	},
};

// Define day total type to avoid TypeScript errors
type DayTotal = {
	conservative: number;
	neutral: number;
	aggressive: number;
	count: number;
};

// Define day order type
type DayOrder = {
	[key: string]: number;
};

// View mode options
export type ViewMode = 'daily' | 'average';

// Define component props
interface ChartContentProps {
	viewMode?: ViewMode;
	className?: string;
	height?: number;
}

export function ChartContent({
	viewMode = 'daily',
	className = 'tw-min-h-[150px] tw-w-full tw-h-52',
	height,
}: ChartContentProps) {
	const {
		property: { propertyId },
	} = usePropertyContext();

	// State to track which series are visible
	const [visibleSeries, setVisibleSeries] = useState<{
		conservative: boolean;
		neutral: boolean;
		aggressive: boolean;
	}>({
		conservative: true,
		neutral: true,
		aggressive: true,
	});

	// State to track container width for responsive behavior
	const [containerWidth, setContainerWidth] = useState<number>(1024);
	const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

	// Effect to handle container resize
	useEffect(() => {
		if (!containerRef) return;

		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				setContainerWidth(entry.contentRect.width);
			}
		});

		resizeObserver.observe(containerRef);
		// Set initial width
		setContainerWidth(containerRef.offsetWidth);

		return () => {
			resizeObserver.disconnect();
		};
	}, [containerRef]);

	// Responsive breakpoints based on container width
	const isMobile = containerWidth < 640;
	const isNarrow = containerWidth < 350; // Only stack when really narrow

	// Handle legend click to toggle series visibility
	const handleLegendClick = (dataKey: string) => {
		setVisibleSeries((prev) => ({
			...prev,
			[dataKey]: !prev[dataKey as keyof typeof prev],
		}));
	};

	const { startDate, endDate } = useMemo(() => {
		const today = new Date();
		// Format the date as YYYY-MM-DD by truncating the time portion
		today.setHours(0, 0, 0, 0);

		const start = new Date(today);

		const end = new Date(today);
		end.setDate(today.getDate() + 120); // Set to today + 120 days

		return { startDate: start, endDate: end };
	}, []); // Empty deps array since we only need to calculate this once

	const { data: dailyRateRecsData, loading } = useGetDailyRateRecsQuery({
		variables: {
			propertyId,
			startDate: startDate.toISOString().split('T')[0], // Just the date part YYYY-MM-DD
			endDate: endDate.toISOString().split('T')[0], // Just the date part YYYY-MM-DD
		},
	});

	const transformedData = useMemo(() => {
		const rateRecs = dailyRateRecsData?.getDailyRateRecs;
		if (!rateRecs?.length) return [];

		const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

		if (viewMode === 'daily') {
			// Return daily data without averaging
			return rateRecs
				.map((rec) => {
					if (!rec) return null;

					const date = new Date(rec.stay_date);
					// Format the date as MM/DD for display
					const formattedDate = `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;

					return {
						day: formattedDate,
						dayOfWeek: days[date.getUTCDay()],
						conservative: rec.conservative_rate,
						neutral: rec.neutral_rate,
						aggressive: rec.aggressive_rate,
						// Store the full date for sorting
						fullDate: date,
					};
				})
				.filter((item): item is NonNullable<typeof item> => item !== null)
				.sort((a, b) => a.fullDate.getTime() - b.fullDate.getTime());
		}

		// Average mode - group by day of week
		const dayTotals: Record<number, DayTotal> = {
			0: { conservative: 0, neutral: 0, aggressive: 0, count: 0 }, // Sunday
			1: { conservative: 0, neutral: 0, aggressive: 0, count: 0 }, // Monday
			2: { conservative: 0, neutral: 0, aggressive: 0, count: 0 }, // Tuesday
			3: { conservative: 0, neutral: 0, aggressive: 0, count: 0 }, // Wednesday
			4: { conservative: 0, neutral: 0, aggressive: 0, count: 0 }, // Thursday
			5: { conservative: 0, neutral: 0, aggressive: 0, count: 0 }, // Friday
			6: { conservative: 0, neutral: 0, aggressive: 0, count: 0 }, // Saturday
		};

		// Sum up the rates for each day of the week
		rateRecs.forEach((rec) => {
			if (!rec) return;

			// Parse the UTC date string
			const date = new Date(rec.stay_date);
			const dayOfWeek = date.getUTCDay(); // Get the day of week from UTC date

			dayTotals[dayOfWeek].conservative += rec.conservative_rate || 0;
			dayTotals[dayOfWeek].neutral += rec.neutral_rate || 0;
			dayTotals[dayOfWeek].aggressive += rec.aggressive_rate || 0;
			dayTotals[dayOfWeek].count += 1;
		});

		// Calculate averages and format the result
		return Object.entries(dayTotals)
			.map(([dayNum, totals]) => {
				const dayIndex = Number.parseInt(dayNum, 10);
				const count = totals.count;

				// Avoid division by zero
				if (count === 0) {
					return {
						day: days[dayIndex],
						conservative: 0,
						neutral: 0,
						aggressive: 0,
					};
				}

				return {
					day: days[dayIndex],
					conservative: Math.round(totals.conservative / count),
					neutral: Math.round(totals.neutral / count),
					aggressive: Math.round(totals.aggressive / count),
				};
			})
			.sort((a, b) => {
				// Ensure days are ordered from Sunday to Saturday
				const dayOrder: DayOrder = {
					Sun: 0,
					Mon: 1,
					Tue: 2,
					Wed: 3,
					Thu: 4,
					Fri: 5,
					Sat: 6,
				};
				return dayOrder[a.day] - dayOrder[b.day];
			});
	}, [dailyRateRecsData, viewMode]);

	// Custom legend component with click handling
	const CustomLegend = (props: any) => {
		const { payload } = props;
		if (!payload) return null;

		// More intelligent stacking logic
		// Estimate space needed: ~100px per legend item + gaps
		const estimatedSpaceNeeded =
			payload.length * 100 + (payload.length - 1) * 24; // 24px for gaps
		const shouldStackVertically =
			containerWidth < estimatedSpaceNeeded && containerWidth < 400;

		return (
			<div
				className={`tw-flex tw-items-center tw-gap-2 tw-mt-4 tw-px-2 ${
					shouldStackVertically
						? 'tw-flex-col tw-gap-2'
						: 'tw-flex-row tw-justify-center tw-gap-6'
				}`}
			>
				{payload.map((entry: any) => {
					const isVisible =
						visibleSeries[entry.dataKey as keyof typeof visibleSeries];
					return (
						<button
							key={entry.dataKey}
							type='button'
							className={`tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-transition-opacity tw-min-w-0 tw-flex-shrink-0 ${
								isVisible ? 'tw-opacity-100' : 'tw-opacity-50'
							}`}
							onClick={() => handleLegendClick(entry.dataKey)}
						>
							<div
								className='tw-w-3 tw-h-3 tw-rounded-full tw-flex-shrink-0'
								style={{ backgroundColor: entry.color }}
							/>
							<span className='tw-text-sm tw-font-medium tw-truncate'>
								{entry.value}
							</span>
						</button>
					);
				})}
			</div>
		);
	};

	if (loading) {
		return <div>Loading data...</div>;
	}

	return (
		<div ref={setContainerRef} className='tw-w-full tw-h-full'>
			<ChartContainer config={chartConfig} className={className}>
				<LineChart
					accessibilityLayer
					data={transformedData}
					margin={{
						left: 3,
						right: 3,
						top: 5,
						bottom: 5,
					}}
					height={height}
				>
					<CartesianGrid vertical={false} />
					<XAxis
						dataKey='day'
						tickLine={false}
						axisLine={false}
						tickMargin={8}
						tickFormatter={(value) => {
							// For mobile screens, show shorter labels
							if (isMobile) {
								return viewMode === 'daily'
									? value.slice(0, 3)
									: value.slice(0, 3);
							}
							return viewMode === 'daily' ? value.slice(0, 5) : value;
						}}
						interval={(() => {
							if (viewMode === 'average') return 0; // Show all days for weekly view

							// For daily view, calculate interval based on container width and data length
							const dataLength = transformedData.length;
							if (dataLength === 0) return 0;

							// Target showing roughly 8-12 labels max
							const targetLabels = isNarrow ? 6 : isMobile ? 8 : 12;
							const calculatedInterval = Math.max(
								0,
								Math.floor(dataLength / targetLabels) - 1,
							);

							return calculatedInterval;
						})()}
					/>
					<YAxis
						domain={([dataMin, dataMax]) => {
							const min = Math.floor(dataMin - 10);
							const max = Math.floor(dataMax + 10);
							return [min, max];
						}}
						orientation='left'
						padding={{ top: 0, bottom: 0 }}
						width={isMobile ? 25 : 30}
						tickFormatter={(value) => {
							// For mobile, show shorter Y-axis labels
							if (isMobile) {
								return `$${Math.round(value)}`;
							}
							return `$${value}`;
						}}
					/>
					<ChartTooltip cursor={false} content={<ChartTooltipContent />} />
					<Legend content={<CustomLegend />} />
					<Line
						dataKey='conservative'
						type='monotone'
						stroke='var(--color-conservative)'
						strokeWidth={2}
						dot={false}
						name='Conservative'
						hide={!visibleSeries.conservative}
					/>
					<Line
						dataKey='neutral'
						type='monotone'
						stroke='var(--color-neutral)'
						strokeWidth={2}
						dot={false}
						name='Neutral'
						hide={!visibleSeries.neutral}
					/>
					<Line
						dataKey='aggressive'
						type='monotone'
						stroke='var(--color-aggressive)'
						strokeWidth={2}
						dot={false}
						name='Aggressive'
						hide={!visibleSeries.aggressive}
					/>
				</LineChart>
			</ChartContainer>
		</div>
	);
}
